import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Spin, Alert } from 'antd';
import galleryIcon from "../../assets/icons/gallery.png";
import village1 from '../../assets/images/gallery/village1.png';
import village2 from '../../assets/images/gallery/village2.png';
import axios from '../../services/axios';

const Gallery = () => {
  const { t, i18n } = useTranslation();

  // Static fallback data with before/after structure
  const staticGallery = [
    {
      id: 1,
      title: i18n.language === 'mr' ? 'गाव विकास १' : 'Village Development 1',
      before_image: village1,
      after_image: village2,
      order: 1
    },
    {
      id: 2,
      title: i18n.language === 'mr' ? 'गाव विकास २' : 'Village Development 2',
      before_image: village1,
      after_image: village2,
      order: 2
    },
  ];

  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    const languageParam = i18n.language === 'mr' ? 'Marathi' : 'English';
    setLoading(true);
    setError(null);

    axios.get('/project-gallery/', { params: { language: languageParam } })
      .then((resp) => {
        // normalize response to an array
        const data = Array.isArray(resp.data)
          ? resp.data
          : (resp.data.results || resp.data.data || Object.values(resp.data || {}));
        if (mounted && Array.isArray(data)) {
          // Sort by order field if available
          const sortedData = data.sort((a, b) => (a.order || 0) - (b.order || 0));
          setGalleryItems(sortedData);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch project-gallery', err);
        if (mounted) setError(err.message || 'Failed to load gallery');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, [i18n.language]);

  return (
    <div className="border bg-white rounded-md p-4 flex flex-col gap-2">
      <div className="flex gap-3 items-center justify-center">
        <div>
          <img
            className="w-10 h-10 border relative"
            src={galleryIcon}
            alt="Projects"
          />
        </div>
        <h2 className="font-semibold w-full m-0 text-2xl max-sm:text-xl">
          {t('Gallery Before and After') || 'पूर्वी आणि नंतर गॅलरी'}
        </h2>
      </div>

      <p className="font-normal text-base">
        {t('Explore our ongoing development initiatives, completed projects, and government schemes designed to improve rural infrastructure and community welfare.') || 'आमच्या चालू विकास उपक्रमांचे, पूर्ण केलेल्या प्रकल्पांचे आणि ग्रामीण पायाभूत सुविधा व समुदाय कल्याण सुधारण्यासाठी तयार केलेल्या सरकारी योजनांचे अन्वेषण करा.'}
      </p>

      <div className="flex flex-col gap-6">
        {loading && (
          <div className="flex justify-center items-center p-8">
            <Spin />
          </div>
        )}

        {error && (
          <div>
            <Alert type="error" message={t('Failed to load gallery') || 'Failed to load gallery'} description={error} />
          </div>
        )}

        {!loading && !error && (
          (() => {
            const items = (galleryItems && galleryItems.length > 0) ? galleryItems : staticGallery;

            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {items.map((item, idx) => (
                  <div
                    key={item.id || idx}
                    className="flex flex-col gap-2 w-full bg-white gallery-pair"
                    style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '12px' }}
                  >
                    {/* Title */}
                    {item.title && (
                      <h3 className="font-medium text-lg text-center m-0" style={{ marginBottom: '8px' }}>
                        {item.title}
                      </h3>
                    )}

                    {/* Before and After Images */}
                    <div className="flex gap-4 w-full justify-center items-center">
                      {/* Before Image */}
                      {item.before_image && (
                        <div className="flex flex-col items-center flex-1">
                          <img
                            src={item.before_image}
                            alt={`${item.title || 'Project'} - ${i18n.language === 'mr' ? 'पूर्वी' : 'Before'}`}
                            className="object-cover rounded-md gallery-img-responsive"
                            style={{ width: '100%', maxHeight: '200px' }}
                          />
                          <span className="text-sm text-gray-500 mt-1">
                            {i18n.language === 'mr' ? 'पूर्वी' : 'Before'}
                          </span>
                        </div>
                      )}

                      {/* After Image */}
                      {item.after_image && (
                        <div className="flex flex-col items-center flex-1">
                          <img
                            src={item.after_image}
                            alt={`${item.title || 'Project'} - ${i18n.language === 'mr' ? 'नंतर' : 'After'}`}
                            className="object-cover rounded-md gallery-img-responsive"
                            style={{ width: '100%', maxHeight: '200px' }}
                          />
                          <span className="text-sm text-gray-500 mt-1">
                            {i18n.language === 'mr' ? 'नंतर' : 'After'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
};

export default Gallery;
